import React, { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IAdHubMetaAccountFormValues } from '../../../types/global/adhubConnections.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_OUTLINE_VARIANT,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';

export interface BrandAdHubMetaAccountFormDialogProps {
    open: boolean;
    submitting?: boolean;
    onClose: () => void;
    onSubmit: (values: IAdHubMetaAccountFormValues) => Promise<boolean> | boolean;
}

const empty: IAdHubMetaAccountFormValues = {
    platform_account_id: '',
    platform_account_name: '',
    facebook_page_id: '',
    instagram_user_id: '',
};

export function BrandAdHubMetaAccountFormDialog({
    open,
    submitting = false,
    onClose,
    onSubmit,
}: BrandAdHubMetaAccountFormDialogProps) {
    const [values, setValues] = useState(empty);

    useEffect(() => {
        if (open) setValues(empty);
    }, [open]);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            color: '#fff',
            bgcolor: BRAND_SURFACE_CONTAINER,
            '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
        },
        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
        mb: 2,
    };

    const canSubmit =
        Boolean(values.platform_account_id.trim()) && Boolean(values.facebook_page_id.trim()) && !submitting;

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                },
            }}>
            <DialogTitle sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                Finish Meta ad account setup
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', mb: 2, lineHeight: 1.5 }}>
                    Paste your Meta Marketing API ad account and Facebook Page IDs. Instagram user ID is recommended for
                    Reels placements.
                </Typography>
                <TextField
                    fullWidth
                    label="Ad account ID"
                    placeholder="act_123456789"
                    value={values.platform_account_id}
                    onChange={e => setValues(v => ({ ...v, platform_account_id: e.target.value }))}
                    helperText="From Meta Business Manager → Ad accounts"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
                    sx={fieldSx}
                />
                <TextField
                    fullWidth
                    label="Ad account name (optional)"
                    value={values.platform_account_name}
                    onChange={e => setValues(v => ({ ...v, platform_account_name: e.target.value }))}
                    sx={fieldSx}
                />
                <TextField
                    fullWidth
                    label="Facebook Page ID"
                    value={values.facebook_page_id}
                    onChange={e => setValues(v => ({ ...v, facebook_page_id: e.target.value }))}
                    helperText="Required for client placement / boost"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
                    sx={fieldSx}
                />
                <TextField
                    fullWidth
                    label="Instagram user ID (optional)"
                    value={values.instagram_user_id}
                    onChange={e => setValues(v => ({ ...v, instagram_user_id: e.target.value }))}
                    sx={fieldSx}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.65)' }}>
                    Cancel
                </Button>
                <Button
                    disabled={!canSubmit}
                    onClick={async () => {
                        const ok = await onSubmit(values);
                        if (ok) onClose();
                    }}
                    sx={{
                        px: 3,
                        py: 1.1,
                        borderRadius: 999,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: BRAND_ON_PRIMARY_FIXED,
                        background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
                        '&.Mui-disabled': { color: 'rgba(0,0,0,0.4)', opacity: 0.5 },
                    }}>
                    {submitting ? (
                        <CircularProgress size={18} sx={{ color: BRAND_ON_PRIMARY_FIXED }} />
                    ) : (
                        'Save connection'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
